// Touro University (New York): public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Touro posts no current Common Data Set (its Office of Institutional
//    Research page links only CDS_2014-2015.xls). The first-year counts come
//    from the U.S. Department of Education's IPEDS Admissions survey for
//    Fall 2024, first-time degree-seeking students (College Navigator and the
//    ADM2024 complete data file, unit 196592, Touro University, New York).
//  - Touro runs admissions college by college ("Each school and program has
//    its own admissions requirements and application process", Touro
//    Admissions). The deadlines, testing and cost here are for Lander College
//    of Arts & Sciences in Flatbush (LAS), the coed undergraduate arts and
//    sciences college: LAS Admissions & Aid (rolling admission, test policy,
//    honors and pathways) and LAS Tuition & Fees (2026-2027 tuition and its
//    "Estimated Program Cost of Attendance, 2026-2027"). Lander College for
//    Men and the New York School of Career and Applied Studies (NYSCAS) are
//    in the programs table from their own admissions pages. None of these
//    pages publishes decision dates.
//  - College Scorecard, school id 196592 (net price by family income), read
//    from the Scorecard API on 2026-10-01; it covers the whole unit, not LAS
//    alone.
// Branding: null. The KB's colours cite a studylib.net copy of Touro
// University California's brand guide, not a Touro New York source, and no
// brand page was found on touro.edu.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=196592#admsns',
        read: '2026-10-01',
    },
    admissions: {
        label: 'Touro University, Admissions',
        url: 'https://www.touro.edu/admissions/',
        read: '2026-10-01',
    },
    las: {
        label: 'Lander College of Arts & Sciences, Admissions & Aid',
        url: 'https://las.touro.edu/admissions--aid/',
        read: '2026-10-01',
    },
    lasTuition: {
        label: 'Lander College of Arts & Sciences, Tuition & Fees and Estimated Cost of Attendance, 2026-2027',
        url: 'https://las.touro.edu/admissions--aid/tuition/',
        read: '2026-10-01',
    },
    lcm: {
        label: 'Lander College for Men, Admissions & Aid',
        url: 'https://lcm.touro.edu/admissions-and-aid/',
        read: '2026-10-01',
    },
    nyscas: {
        label: 'New York School of Career and Applied Studies, Admissions & Aid',
        url: 'https://nyscas.touro.edu/admissions--aid/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?196592-Touro-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'touro-university',
    kbId: 'touro_university',
    name: 'Touro University',
    shortName: 'Touro',
    location: 'New York, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // No verified Touro New York brand source (the KB cites a third-party copy
    // of Touro University California's guide), so the app's neutral is used.
    branding: null,

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 2152,
        admitted: 1309,
        enrolled: 569,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates, all of Touro\'s New York undergraduate colleges together (Touro posts no current Common Data Set)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'IPEDS reports one first-year acceptance rate for Touro\'s New York undergraduate colleges together, and Touro publishes no rate by college or major.',
        source: 'ipeds',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Each Touro college runs its own admissions. The deadlines, testing and cost below are for Lander College of Arts & Sciences in Flatbush; other colleges and programs differ:',
        rows: [
            { school: 'Lander College of Arts & Sciences honors and pathways', requirement: 'The Pathway Honors Programs, the Society of Fellows and every pre-professional pathway take a separate application with its own timeline. Pathway Honors applicants must send SAT or ACT scores.', source: 'las' },
            { school: 'Lander College for Men', requirement: 'Rolling admission for the fall, winter or summer term, with SAT or ACT scores not required for the standard application. Eligible candidates are interviewed for the yeshiva portion, and the medical honors pathway and integrated honors track take an additional application.', source: 'lcm' },
            { school: 'NYSCAS radiography and sonography', requirement: 'Most NYSCAS programs admit year round, but these two competitive programs accept applications only from December 1 to March 31 for fall enrollment.', source: 'nyscas' },
        ],
        source: 'admissions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Admission', value: 'Rolling' },
        rows: [
            { plan: 'Rolling admission', deadline: 'Apply any time', decision: null, binding: false },
        ],
        note: 'Most Lander College of Arts & Sciences programs admit on a rolling basis, so you can apply and start in fall, spring, post-Pesach or summer. Pre-professional and honors pathways may have their own timelines. Some applicants are asked to interview with an admissions officer before the Admissions Committee decides. LAS does not publish decision dates.',
        source: 'las',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'At Lander College of Arts & Sciences, SAT and ACT scores are not required for general admission, but they can help with scholarships. Applicants to a Pathway Honors Program must send scores; the Society of Fellows does not require them.',
        source: 'las',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'las',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 18940 },
            { item: 'Fees', amount: 500 },
            { item: 'Housing and food (living off campus, estimate)', amount: 25128 },
            { item: 'Transportation', amount: 1674 },
            { item: 'Personal expenses', amount: 3222 },
            { item: 'Books and supplies', amount: 834 },
            { item: 'Loan origination fee', amount: 150 },
            { item: 'Computer allowance (first semester)', amount: 750 },
        ],
        total: 51198,
        note: 'Touro\'s sample cost of attendance for a full-time student at Lander College of Arts & Sciences in Flatbush (12 to 18 semester hours, fall and spring), living off campus. Living with a parent, Touro estimates $31,965. The Accounting Institute charges lower tuition.',
        source: 'lasTuition',
        netPrice: {
            average: 28433,
            byIncome: [
                { income: '$0 to $30,000', amount: 26020 },
                { income: '$30,001 to $48,000', amount: 26761 },
                { income: '$48,001 to $75,000', amount: 25631 },
                { income: '$75,001 to $110,000', amount: 30003 },
                { income: 'Over $110,000', amount: 32854 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Touro\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
